import { Crosshair1Icon } from '@radix-ui/react-icons'
import { ThreeEvent } from '@react-three/fiber'
import { uuid4 } from '@sentry/core'
import {
  MapFeatureType,
  useFeatureInteractionContext,
} from 'features/siteMap/contexts/FeatureInteractionContext'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import isNil from 'lodash/isNil'
import React, { FC, useState } from 'react'
import { useMount, useUnmount } from 'react-use'
import styled from 'styled-components'
import { Vector2 } from 'three'
import { SiteFeaturePrimitive, SiteModelCoordinate } from 'types'

const SelectOnMapButton = styled.div<{ $isSelecting: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  font-family: sans-serif;
  font-style: ${({ $isSelecting }) => ($isSelecting ? 'italic' : 'normal')};
  background-color: ${({ $isSelecting, theme }) =>
    $isSelecting ? theme.forecast.activeButton : ''};
  width: fit-content;
  padding: 4px 8px;

  &:hover {
    background-color: ${({ theme, $isSelecting }) =>
      $isSelecting ? 'rgba(255, 255, 255, 0.3)' : theme.forecast.hoverButton};
  }
`

interface SelectCoordinatesOnMapButtonProps {
  minimized: boolean
  feature: { primitive: SiteFeaturePrimitive; type: MapFeatureType } | undefined
}

export const SelectCoordinatesOnMapButton: FC<
  SelectCoordinatesOnMapButtonProps
> = (props) => {
  const { setSiteViewFeatureGenerationConfig } = useFeatureInteractionContext()
  const { updateFeaturePrimitive } = useTilesetMapFeatureHelpers()
  const { setSelectedFeature, setShowFeaturePopupMenu } =
    useFeatureInteractionContext()

  const [isSelectingOnMap, setIsSelectingOnMap] = useState(false)

  useMount(() => {
    if (!props.feature) {
      startSelectOnMapFlow()
      setIsSelectingOnMap(true)
    }
  })

  useUnmount(() => {
    setSiteViewFeatureGenerationConfig(undefined)
    setIsSelectingOnMap(false)
  })

  const createNewPrimitive = (coordinate: SiteModelCoordinate) => {
    return {
      id: 'feature_' + uuid4().slice(0, 8),
      name: '',
      kind: 'cylinder',
      coordinate: coordinate,
      rotation: [0, 0, 0],
      geometry: {
        bottom_radius: 20,
        top_radius: 20,
        height: 20,
        theta_start: null,
        theta_end: null,
      },
      unit_id: null,
      area_id: null,
    } as SiteFeaturePrimitive
  }

  const raycastCallback = (
    e: ThreeEvent<MouseEvent>,
    coordinate: SiteModelCoordinate
  ) => {
    let newFeature = props.feature?.primitive

    if (!newFeature) {
      setShowFeaturePopupMenu(new Vector2(e.clientX, e.clientY))
      newFeature = createNewPrimitive(coordinate)
    } else {
      newFeature = {
        ...newFeature,
        coordinate: coordinate,
      }
    }
    setSelectedFeature(
      isNil(newFeature.unit_id)
        ? {
            type: MapFeatureType.Unknown,
            featureID: newFeature.id,
          }
        : {
            type: MapFeatureType.Unit,
            unitID: newFeature.unit_id,
          }
    )
    updateFeaturePrimitive(newFeature)

    setSiteViewFeatureGenerationConfig(undefined)
    setIsSelectingOnMap(false)
  }

  const startSelectOnMapFlow = () => {
    setSiteViewFeatureGenerationConfig({
      primitive:
        props.feature?.primitive ??
        createNewPrimitive({ lat: 0, lon: 0, height: 0 }),
      clickCallback: raycastCallback,
    })
  }

  return (
    <SelectOnMapButton
      $isSelecting={isSelectingOnMap}
      onClick={() => {
        const willBeSelecting = !isSelectingOnMap
        if (willBeSelecting) {
          startSelectOnMapFlow()
        } else {
          setSiteViewFeatureGenerationConfig(undefined)
        }
        setIsSelectingOnMap(willBeSelecting)
      }}>
      <Crosshair1Icon height={24} width={24} />
      {!props.minimized &&
        (isSelectingOnMap ? 'Click map to place...' : 'Select on map')}
    </SelectOnMapButton>
  )
}
